import React, { useEffect, useRef, useState } from 'react';
import { DateParam, NumberParam, useQueryParam } from 'use-query-params';
import { Opt } from '@/features/details/date-pick/quick-range-select';
import { ExpVersion } from '@/webapi/use-analytics-api';
import { Experience } from '@/webapi/use-experience-api';
import { calcStartDate } from '@/features/details/experiment-details';
import { isValid } from '@/utils/data_freshness';

interface DatePickerContext {
  step: Opt;
  setStep: (x: Opt) => void;
  setRanges: (
    x: Array<{ startDate: Date; endDate: Date; key: string }>,
  ) => void;
  versions: Array<ExpVersion>;
  setVersion: (value: ExpVersion | undefined) => void;
  startDate: Date;
  endDate: Date;
  version: ExpVersion;
  ranges: Array<{ startDate: Date; endDate: Date; key: string }>;
  showVersionsSelect: boolean;
  showQuickSelect: boolean;
  showCustomDatePicker: boolean;
  quickSelect: () => void;
  customRange: () => void;
  versionsSelect: () => void;
}

export const DatePickerContext = React.createContext({} as DatePickerContext);

export function newDatePickerContext(experience: Experience) {
  const [step, setStep] = useQueryParam<Opt>(`o`, NumberParam);
  const [versionNumber, setVersionNumber] = useQueryParam<Opt>(
    `v`,
    NumberParam,
  );
  const [queryStart, setQueryStart] = useQueryParam(`s`, DateParam);
  const [queryEnd, setQueryEnd] = useQueryParam(`e`, DateParam);
  const expEndDate = getExpEndDate(experience);
  const [init, setInit] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setInit(true);
    }, 2000);
  }, []);

  const versionChanged = useRef(false);
  const [version, _setVersion] = useState<ExpVersion | undefined>({
    ...experience.currentVersion,
    isCurrent: true,
    expEndDate,
  });
  const versions = getExperienceVersions(experience, expEndDate);

  useEffect(() => {
    if (experience?.isHidden) {
      return;
    }
    const version = versions?.find((v) => v.version === versionNumber);
    if (version) {
      _setVersion(version);
    }
  }, [versionNumber]);

  const rangeChanged = useRef<boolean>(false);

  const setVersion = (v: ExpVersion) => {
    if (experience?.isHidden) {
      return;
    }
    _setVersion(v);
    versionChanged.current = true;
    setVersionNumber(v?.version || -1);
    setTimeout(() => setVersionNumber(v?.version || -1), 110);
  };
  const [ranges, _setRanges] = useState([
    {
      startDate: calcDefaultStartDate(queryStart, experience),
      endDate: queryEnd || new Date(),
      key: `rollup`,
    },
  ]);
  const setRanges = (x: any) => {
    rangeChanged.current = true;
    _setRanges(x);
  };

  useEffect(() => {
    if (!init) {
      return;
    }
    if (rangeChanged.current || step === Opt.CustomRange) {
      setQueryStart(new Date(ranges?.[0]?.startDate));
      setQueryEnd(new Date(ranges?.[0]?.endDate));
      setTimeout(() => {
        setQueryStart(new Date(ranges?.[0]?.startDate));
        setQueryEnd(new Date(ranges?.[0]?.endDate));
      }, 110);
    }
  }, [ranges?.[0]?.startDate, ranges?.[0]?.endDate]);
  useEffect(() => {
    if (experience?.isHidden) {
      return;
    }
    if (version?.version && versionChanged.current) {
      setQueryStart(new Date(version.startDate));
      setQueryEnd(getVersionEndDate(version));
      setTimeout(() => {
        setQueryStart(new Date(version.startDate));
        setQueryEnd(getVersionEndDate(version));
      }, 110);
    }
  }, [version?.version]);

  const startDate = calcStartDate(
    queryStart,
    ranges?.[0]?.startDate,
    experience?.metricsStartAt,
  );
  const endDate = calcEndDate(queryEnd, ranges, expEndDate);

  useEffect(() => {
    if (!step) {
      setStep(Opt.Current);
    }
  }, []);

  const [showVersionsSelect, setShowVersionsSelect] = useState(
    showPrevVersion(step),
  );

  const [showCustomDatePicker, setShowCustomDatePicker] = useState(
    showCustomRange(step),
  );

  const [showQuickSelect, setShowQuickSelect] = useState(shoQuickSelect(step));

  useEffect(() => {
    setShowQuickSelect(shoQuickSelect(step));
    setShowCustomDatePicker(showCustomRange(step));
    setShowVersionsSelect(showPrevVersion(step));
  }, [step]);

  useEffect(() => {
    if (init) {
      handleQuickSelectChanged(
        step,
        setRanges,
        versions,
        setVersion,
        setStep,
      )();
    }
  }, [step]);

  const quickSelect = () => {
    setShowVersionsSelect(false);
    setShowCustomDatePicker(false);
    setShowQuickSelect(true);
  };

  const customRange = () => {
    setShowVersionsSelect(false);
    setShowCustomDatePicker(true);
    setShowQuickSelect(false);
  };

  const versionsSelect = () => {
    setShowVersionsSelect(true);
    setShowCustomDatePicker(false);
    setShowQuickSelect(false);
  };

  useEffect(() => {
    const allFalse =
      !showQuickSelect && !showVersionsSelect && !showCustomDatePicker;
    if (allFalse) {
      setShowQuickSelect(true);
    }
  }, [showQuickSelect, showVersionsSelect, showCustomDatePicker]);
  return {
    quickSelect,
    customRange,
    versionsSelect,
    showQuickSelect,
    showVersionsSelect,
    showCustomDatePicker,
    ranges,
    setRanges,
    endDate,
    startDate,
    version,
    versions,
    setVersion,
    step,
    setStep,
  };
}

function handleQuickSelectChanged(
  step: Opt,
  setRanges: (
    x: Array<{ startDate: Date; endDate: Date; key: string }>,
  ) => void,
  versions: Array<ExpVersion>,
  setVersion: (value: ExpVersion | undefined) => void,
  setStep: (x: Opt) => void,
) {
  return () => {
    if (!step) {
      setStep(Opt.Current);
    }
    switch (step) {
      case Opt.Current:
        {
          const current = versions?.find((v) => v?.isCurrent);
          const startDate = new Date(current?.startDate);
          const endDate = new Date(getVersionEndDate(current));
          if (isValid(startDate) && isValid(endDate))
            setRanges([
              {
                startDate,
                endDate,
                key: `rollup`,
              },
            ]);
          setVersion(current);
        }
        break;
      case Opt.Today:
        setRanges([
          { startDate: new Date(), endDate: new Date(), key: `rollup` },
        ]);
        setVersion(undefined);
        break;
      case Opt.Yesterday:
        setRanges([
          {
            startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
            endDate: new Date(new Date().setDate(new Date().getDate() - 1)),
            key: `rollup`,
          },
        ]);
        setVersion(undefined);
        break;
      case Opt.LastWeek:
        setRanges([
          {
            startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
            endDate: new Date(),
            key: `rollup`,
          },
        ]);
        setVersion(undefined);
        break;
      case Opt.LastMonth:
        setRanges([
          {
            startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
            endDate: new Date(),
            key: `rollup`,
          },
        ]);
        setVersion(undefined);
        break;
      case Opt.OverAll:
        {
          const startDate = new Date(
            versions?.find((v) => v.version === 1)?.startDate,
          );
          if (isValid(startDate))
            setRanges([
              {
                startDate,
                endDate: new Date(),
                key: `rollup`,
              },
            ]);
          setVersion(undefined);
        }
        break;
      default:
    }
  };
}

function calcDefaultStartDate(s: Date, experience: Experience) {
  return (
    s ||
    (experience?.metricsStartAt && new Date(experience?.metricsStartAt)) ||
    (experience?.publishedAt && new Date(experience?.publishedAt)) ||
    new Date()
  );
}

function shoQuickSelect(step: Opt) {
  return [
    Opt.Current,
    Opt.Today,
    Opt.Yesterday,
    Opt.LastWeek,
    Opt.LastMonth,
    Opt.OverAll,
  ].includes(step);
}

function showCustomRange(step: Opt) {
  return step === Opt.CustomRange;
}

function showPrevVersion(step: Opt) {
  return step === Opt.PreviousVersion;
}

function getExpEndDate(experience: Experience): Date | undefined {
  const experienceEndDate = new Date(
    experience?.archivedAt || experience?.endDate,
  );
  return isValid(experienceEndDate) ? experienceEndDate : undefined;
}

function getVersionEndDate(version: ExpVersion) {
  let endDate = new Date(version?.endDate);
  if (version?.expEndDate) {
    endDate = new Date(
      Math.min(version?.expEndDate?.valueOf(), endDate?.valueOf()),
    );
  }
  return endDate;
}

function calcEndDate(
  queryEnd: Date,
  ranges: { endDate: Date; startDate: Date; key: string }[],
  expEndDate: Date,
) {
  let endDate = queryEnd || new Date(ranges?.[0]?.endDate);
  if (endDate?.valueOf() > expEndDate?.valueOf()) {
    endDate = new Date(expEndDate.valueOf());
  }
  return endDate;
}

function getExperienceVersions(experience: Experience, expEndDate: Date) {
  return experience.versions?.map((v) => {
    if (v?.version === experience?.currentVersion?.version) {
      return {
        ...v,
        isCurrent: true,
        expEndDate,
      };
    }
    return { ...v, expEndDate };
  }) as Array<ExpVersion>;
}
